import { fhirR4 } from "@smile-cdr/fhirts";

// Structured dose / frequency / timing options for the medication form.
//
// Mirrors the FHIR R4 `Dosage` shape the Engage backend adopted in VEG-535:
//   dose       -> Dosage.doseAndRate.doseQuantity (value + UCUM unit)
//   frequency  -> Dosage.timing.repeat { frequency, period, periodUnit }
//   interval   -> Dosage.timing.repeat.periodMax ("every 4 to 6 hours")
//   one-time   -> Dosage.timing.repeat.count = 1
//   PRN        -> Dosage.asNeededBoolean, additive; the count stays in frequency
//   ceiling    -> Dosage.maxDosePerPeriod (Ratio)
//   timing     -> Dosage.timing.repeat.when (EventTiming codes)
// See src/utils/dosage-steps.ts for the round-trip mappers.
//
// The `label` fields are the English forms used for the stored `dosage.text`
// SIG (which is kept in English). UI display is translated via the `enums.*`
// namespace in the locale files (e.g. enums.doseUnit.<value>), keyed by `value`.

/**
 * The unit a curator picks when they do not know it. Enforcing a unit choice is
 * what stops a bare "10" being saved as a dose, so there has to be an honest way
 * to say the unit is unknown rather than leaving the field blank.
 *
 * Not a UCUM unit and not a counted form: it asserts no code, and a strength
 * cannot be quoted per one of them.
 */
export const UNKNOWN_UNIT = "unknown";

/** Placeholder for a select with nothing chosen yet. One spelling everywhere. */
export const NO_SELECTION_LABEL = "—";

/**
 * The denominator Engage synthesises for a legacy record with no stated form.
 * Nobody chose it, so it never seeds the Form picker.
 */
export const LEGACY_EACH_DENOMINATOR = "each";

export const DOSE_UNITS = [
  // Mass / volume / concentration strengths (true UCUM units).
  { value: "mg", label: "mg", ucumCode: "mg", ucum: true, counted: false, volume: false },
  { value: "g", label: "g", ucumCode: "g", ucum: true, counted: false, volume: false },
  { value: "mcg", label: "mcg", ucumCode: "ug", ucum: true, counted: false, volume: false },
  { value: "ng", label: "ng", ucumCode: "ng", ucum: true, counted: false, volume: false },
  { value: "mL", label: "mL", ucumCode: "mL", ucum: true, counted: false, volume: true },
  { value: "L", label: "L", ucumCode: "L", ucum: true, counted: false, volume: true },
  { value: "meq", label: "mEq", ucumCode: "meq", ucum: true, counted: false, volume: false },
  { value: "IU", label: "IU", ucumCode: "[iU]", ucum: true, counted: false, volume: false },
  { value: "%", label: "%", ucumCode: "%", ucum: true, counted: false, volume: false },
  // Administration forms (counted units — no asserted UCUM system/code).
  { value: "tablet", label: "tablet(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "capsule", label: "capsule(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "drop", label: "drop(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "puff", label: "puff(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "spray", label: "spray(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "application", label: "application(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "inhalation", label: "inhalation(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "applicatorful", label: "applicatorful(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "packet", label: "packet(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: "unit", label: "unit(s)", ucumCode: "", ucum: false, counted: true, volume: false },
  { value: UNKNOWN_UNIT, label: "unknown", ucumCode: "", ucum: false, counted: false, volume: false },
] as const;

export type DoseUnitOption = (typeof DOSE_UNITS)[number];

/**
 * The two families every picklist below is built from, and the reason the lists
 * differ at all: a measured unit says how much drug there is, a counted one says
 * how many of the thing it comes in. Confusing the two is what VMD-713 fixes, so
 * no field offers both unless it genuinely takes both.
 *
 * Shared with Engage (VEG-760); the five lists have to match across both apps.
 */
export const MEASURED_UNITS: readonly DoseUnitOption[] = DOSE_UNITS.filter(unit => unit.ucum);
export const COUNTED_UNITS: readonly DoseUnitOption[] = DOSE_UNITS.filter(unit => unit.counted);

/** Spread rather than appended, so "unknown" always lands last in a picklist. */
const UNKNOWN_UNIT_OPTION: readonly DoseUnitOption[] = DOSE_UNITS.filter(unit => unit.value === UNKNOWN_UNIT);

/** A strength is always a measured amount ("875 mg"), or an admitted unknown. */
export const STRENGTH_UNITS: readonly DoseUnitOption[] = [...MEASURED_UNITS, ...UNKNOWN_UNIT_OPTION];

/**
 * Denominator of a strength Ratio: "875 mg per 1 **tablet**", "250 mg per
 * **5 mL**". Takes both families, since a strength is quoted either per item or
 * per volume. No unknown: "875 mg per 1 unknown" says nothing, and the field is
 * optional, so leaving it blank is the honest answer.
 */
export const STRENGTH_PER_UNITS: readonly DoseUnitOption[] = [...MEASURED_UNITS, ...COUNTED_UNITS];

/**
 * The measured units that are also a per-administration amount. A patient is
 * handed 5 mL of suspension or 10 IU of insulin; nobody is handed 5 mg.
 */
const ADMINISTERED_MEASURED_UNITS: ReadonlySet<string> = new Set(["mL", "L", "IU"]);

/**
 * What one administration can be counted in. Administration forms first, because
 * putting mg within reach is how it ended up in the quantity field in the first
 * place: curators recognise it and pick it before reading the label.
 */
const ADMINISTERED_UNITS: readonly DoseUnitOption[] = [
  ...COUNTED_UNITS,
  ...MEASURED_UNITS.filter(unit => ADMINISTERED_MEASURED_UNITS.has(unit.value)),
];

/** Quantity taken at one time ("1 tablet", "5 mL"), or an admitted unknown. */
export const QUANTITY_UNITS: readonly DoseUnitOption[] = [...ADMINISTERED_UNITS, ...UNKNOWN_UNIT_OPTION];

/**
 * The dose form, chosen once per medication and the single source of the noun
 * in "Each tablet contains 3 mg" and "Takes 1 to 2 tablets". Drives the strength
 * denominator, the quantity and the amount ceiling, so it offers exactly what
 * all three have in common: the counted forms plus the volumes a dose is
 * measured in.
 *
 * Carries `unknown` because the field is required: a source that never named a
 * form still has to be recordable, and "Unknown" is how a curator says so
 * explicitly rather than by leaving a blank. It stores as the `unknown` literal
 * on the quantity, and counts as no named form for the strength denominator and
 * `Medication.form`, neither of which can say anything useful about it.
 */
export const FORM_UNITS: readonly DoseUnitOption[] = [...ADMINISTERED_UNITS, ...UNKNOWN_UNIT_OPTION];

/** True when the form names an actual dose form, rather than an admitted unknown. */
export const isNamedForm = (form: string): boolean => !!form && form !== UNKNOWN_UNIT;

/**
 * The measured units that are a volume rather than a mass or a count. Derived
 * from the option list like every other grouping here, so adding a volume to
 * `DOSE_UNITS` does not need a second edit somewhere else.
 */
const VOLUME_UNITS: ReadonlySet<string> = new Set(DOSE_UNITS.filter(unit => unit.volume).map(unit => unit.value));

/**
 * Whether a strength quoted against this form needs its denominator shown.
 *
 * A solid dose is quoted per one of itself ("875 mg per 1 tablet") and the "1"
 * is noise. A liquid is quoted per an arbitrary volume ("250 mg per 5 mL"), so
 * that number is real content and has to be editable.
 */
export const formQuotesAVolume = (form: string): boolean => VOLUME_UNITS.has(form);

/**
 * Amount ceiling ("max 8 tablets per day"). A ceiling is a quantity, so it takes
 * exactly what a quantity takes. No unknown: a limit nobody can name is not a
 * limit, and the whole block is optional.
 */
export const MAX_DOSE_UNITS: readonly DoseUnitOption[] = ADMINISTERED_UNITS;

/**
 * Infusion rate numerator ("100 mL per hour"). A rate is a measured amount over
 * time, never a count of tablets, and never an unknown: without the unit there
 * is no rate to store.
 */
export const RATE_UNITS: readonly DoseUnitOption[] = MEASURED_UNITS;

export type DoseUnit = (typeof DOSE_UNITS)[number]["value"];

/** Value plus the English label a unit select needs to render one option. */
export interface UnitOption {
  value: string;
  label: string;
}

/**
 * Options a unit select should offer for the value it currently holds.
 *
 * A unit already stored on the record is appended when the field's own list does
 * not carry it, so narrowing a list cannot make an existing record's unit
 * unselectable and drop it on the next save. A value we do not recognise at all
 * (an EMR-sourced unit, say) becomes its own option rather than leaving the
 * select holding a value with no matching item.
 */
export const unitOptionsFor = (units: readonly UnitOption[], current: string): readonly UnitOption[] => {
  if (!current || units.some(unit => unit.value === current)) {
    return units;
  }
  const known = DOSE_UNITS.find(unit => unit.value === current);
  return [...units, known ?? { value: current, label: current }];
};

// `count` is the one-off administration count (`timing.repeat.count`); it is
// null for every recurring preset. "As needed" is deliberately absent — PRN is
// an additive flag on top of a schedule, not a frequency of its own (VMD-702).
export const FREQUENCY_PRESETS = [
  { value: "once_daily", label: "Once daily", frequency: 1, period: 1, periodUnit: "d", count: null },
  { value: "twice_daily", label: "Twice daily", frequency: 2, period: 1, periodUnit: "d", count: null },
  { value: "three_times_daily", label: "Three times daily", frequency: 3, period: 1, periodUnit: "d", count: null },
  { value: "every_12_hours", label: "Every 12 hours", frequency: 1, period: 12, periodUnit: "h", count: null },
  { value: "every_other_day", label: "Every other day", frequency: 1, period: 2, periodUnit: "d", count: null },
  { value: "once_weekly", label: "Once weekly", frequency: 1, period: 1, periodUnit: "wk", count: null },
  { value: "once_monthly", label: "Once monthly", frequency: 1, period: 1, periodUnit: "mo", count: null },
  { value: "one_time_only", label: "One time only", frequency: null, period: null, periodUnit: null, count: 1 },
  { value: "custom", label: "Custom", frequency: null, period: null, periodUnit: null, count: null },
] as const;

export type FrequencyPresetValue = (typeof FREQUENCY_PRESETS)[number]["value"];

export const FREQUENCY_PERIOD_UNITS = [
  { value: "h", label: "hour(s)" },
  { value: "d", label: "day(s)" },
  { value: "wk", label: "week(s)" },
  { value: "mo", label: "month(s)" },
] as const;

export type FrequencyPeriodUnit = (typeof FREQUENCY_PERIOD_UNITS)[number]["value"];

/**
 * Denominator periods for `Dosage.maxDosePerPeriod` ("max 8 tablets per day").
 * The denominator is always a count of 1 of the chosen period.
 */
export const MAX_DOSE_PERIOD_UNITS = FREQUENCY_PERIOD_UNITS;

/**
 * Denominator periods for an infusion rate (`doseAndRate.rateRatio`). Its own
 * list rather than the frequency one: infusions are quoted per minute or per
 * hour, and never per week or per month.
 */
export const RATE_PERIOD_UNITS = [
  { value: "min", label: "minute(s)" },
  { value: "h", label: "hour(s)" },
  { value: "d", label: "day(s)" },
] as const;

/**
 * `Dosage.additionalInstruction` options — supplemental instructions and
 * warnings to the patient ("with meals", "may cause drowsiness").
 *
 * SNOMED CT, from the R4 additional-instruction-codes value set. That set is an
 * intensional filter (`is-a 419492006`) rather than an enumerated list, so there
 * is nothing to bind to without a terminology server; the binding is Example
 * strength, so this is a curated administration-relevant subset. The published
 * expansion also carries UK dispensing-label warnings and abstract fragments
 * ("Times", "Each"), which are deliberately left out.
 *
 * `display` is the official SNOMED display, stored on the coding. UI labels are
 * translated via `enums.additionalInstruction.<code>`.
 */
export const ADDITIONAL_INSTRUCTIONS = [
  { code: "311504000", display: "With or after food" },
  { code: "311501008", display: "Half to one hour before food" },
  { code: "717154004", display: "Take on an empty stomach" },
  { code: "419303009", display: "With plenty of water" },
  { code: "418693002", display: "Swallowed whole, not chewed" },
  { code: "418991002", display: "Sucked or chewed" },
  { code: "419529008", display: "Dissolved under the tongue" },
  { code: "417995008", display: "Dissolve or mix with water before taking" },
  { code: "418577003", display: "Take at regular intervals. Complete the prescribed course unless otherwise directed" },
  { code: "421769005", display: "Follow directions" },
] as const;

export type AdditionalInstructionCode = (typeof ADDITIONAL_INSTRUCTIONS)[number]["code"];

export const ADDITIONAL_INSTRUCTION_CODES: ReadonlySet<string> = new Set(ADDITIONAL_INSTRUCTIONS.map(i => i.code));

// EventTiming codes (FHIR R4): MORN | AFT | EVE | HS | AC | PC.
export const TIMING_OPTIONS = [
  { value: "MORN", label: "Morning" },
  { value: "AFT", label: "Afternoon" },
  { value: "EVE", label: "Evening" },
  { value: "HS", label: "Bedtime" },
  { value: "AC", label: "Before Meals" },
  { value: "PC", label: "After Meals" },
] as const;

export type TimingCode = (typeof TIMING_OPTIONS)[number]["value"];

/** The subset of `Timing.repeat` the frequency control round-trips. */
export interface FrequencyRepeat {
  frequency?: number | null;
  /** PRN ceiling ("up to 2 times a day") — occupies the same slot as `frequency`. */
  /** Legacy only — read so an older record still shows its count. Never written. */
  frequencyMax?: number | null;
  period?: number | null;
  periodMax?: number | null;
  periodUnit?: string | null;
  count?: number | null;
}

/**
 * Derives a frequency preset value from structured `Timing.repeat` fields.
 * Returns "" when there is no schedule to describe (e.g. a PRN-only dosage),
 * and "custom" when a schedule is present but matches no preset.
 */
export const getFrequencyPreset = (repeat: FrequencyRepeat = {}): FrequencyPresetValue | "" => {
  const { frequency, frequencyMax, period, periodMax, periodUnit, count } = repeat;

  // A one-off administration carries a count and no recurrence.
  if (count === 1 && !frequency && !frequencyMax && !period) {
    return "one_time_only";
  }

  // An interval range ("every 4 to 6 hours") has no preset equivalent.
  if (periodMax != null) {
    return "custom";
  }

  // `frequency` is where the count lives. A stored `frequencyMax` only appears
  // on records written before VMD-699 reversed that rule, and is read here so
  // such a record still shows its count — it is never written back.
  const effectiveFrequency = frequency ?? frequencyMax ?? null;
  if (effectiveFrequency === null && period == null && periodUnit == null) {
    return "";
  }

  const match = FREQUENCY_PRESETS.find(
    p => p.frequency === effectiveFrequency && p.period === period && p.periodUnit === periodUnit
  );
  return match?.value ?? "custom";
};

/*
Determine friendly frequency value from the FHIR-formatted dosage
INPUT EX for 3x/day med: "timing": { "repeat": { "frequency": 3, "period": 1, "periodUnit": "d", "when": ["MORN","AFT","EVE"]}},

Object key/value pairs:
{ '<frequency>': { '<period>' : { '<periodUnit>': '<translation>' } } => { 3: { 1: { d: 'three_times_daily' }}}
*/
export const MEDICATION_FREQUENCY_MAP: Record<
  number,
  Record<number, Partial<Record<fhirR4.TimingRepeat.PeriodUnitEnum, string>>>
> = {
  // Fallback starts
  0: {
    0: {
      s: "custom",
      min: "custom",
      h: "custom",
      d: "custom",
      wk: "custom",
      mo: "custom",
      a: "custom",
    },
  },
  // Fallback ends
  1: {
    1: {
      d: "once_daily",
      wk: "once_weekly",
      mo: "once_monthly",
    },
    2: {
      d: "every_other_day",
    },
    12: {
      h: "every_12_hours", // TODO: Change to 'every_twelve_hours' to be consistent w others (do so for FREQUENCY_PRESETS too)
    },
  },
  2: {
    1: {
      d: "twice_daily",
    },
  },
  3: {
    1: {
      d: "three_times_daily",
    },
  },
  4: {
    1: {
      d: "four_times_daily",
    },
  },
};

/**
  ┌───────────────────┬─────────┬───────────┐
  │      preset       │  calc   │ doses/day │
  ├───────────────────┼─────────┼───────────┤
  │ once_monthly      │ 1 / 30  │ 0.033     │
  ├───────────────────┼─────────┼───────────┤
  │ once_weekly       │ 1 / 7   │ 0.143     │
  ├───────────────────┼─────────┼───────────┤
  │ every_other_day   │ 1 / 2   │ 0.5       │
  ├───────────────────┼─────────┼───────────┤
  │ once_daily        │ 1 / 1   │ 1         │
  ├───────────────────┼─────────┼───────────┤
  │ twice_daily       │ 2 / 1   │ 2         │
  ├───────────────────┼─────────┼───────────┤
  │ every_12_hours    │ 1 / 0.5 │ 2         │
  ├───────────────────┼─────────┼───────────┤
  │ three_times_daily │ 3 / 1   │ 3         │
  └───────────────────┴─────────┴───────────┘
 */

const PERIOD_UNIT_TO_DAYS: Record<FrequencyPeriodUnit, number> = {
  h: 1 / 24,
  d: 1,
  wk: 7,
  mo: 30,
};

/**
 * Doses per day for a preset — the comparable "how often" metric.
 * as_needed / custom (null frequency) return null: they have no fixed rate.
 *
 * Ex.
 * const a = getDosesPerDay(1, 1, "d"); // once_daily  -> 1
 * const b = getDosesPerDay(3, 1, "d"); // three_times -> 3
 *  a < b; // true
 */
export const getDosesPerDay = (
  frequency?: number | null,
  period?: number | null,
  periodUnit?: string | null
): number | null => {
  if (frequency == null || period == null || !periodUnit) {
    return null;
  }
  const days = PERIOD_UNIT_TO_DAYS[periodUnit as FrequencyPeriodUnit];
  if (!days) {
    return null;
  }
  return frequency / (period * days);
};

/*
  getPresetDosesPerDay returns null for as_needed/custom (no fixed rate). My guards above make an incomparable frequency contribute nothing to
  validity — so if the frequency is as_needed, the step is only valid via the dose change. That's the safe default (you don't want null > 3
  silently passing). If a custom frequency should still be comparable, that's a separate task (you'd compute doses/day from
  frequencyTimes/frequencyEvery/frequencyUnit instead).
*/
export const getPresetDosesPerDay = (presetValue?: string | null): number | null => {
  const preset = FREQUENCY_PRESETS.find(p => p.value === presetValue);
  return preset ? getDosesPerDay(preset.frequency, preset.period, preset.periodUnit) : null;
};
